import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  User,
  Gamepad2,
  Settings,
  MessageSquare,
  Activity,
  Shield,
  LogOut,
  Menu,
  X,
  Grid3X3,
  Trophy,
  Ticket,
  Building,
  Gift,
  ListOrdered,
  AtSign,
  UserCog,
  Receipt,
  FileText,
  Crown,
  TestTube,
  Code,
  UserPlus,
  Wallet,
  Star,
  Clock,
  KeyRound,
  Radio,
  Ban,
  Image as ImageIcon,
  ChevronDown,
  SlidersHorizontal,
  MessageSquareText,
  Type,
  Scale,
  Video,
  ClipboardList,
  Dices,
  ShoppingCart, ChefHat, UtensilsCrossed, BarChart3,
} from 'lucide-react';
import { useState } from 'react';

// Nav grouped into collapsible sections. Each item is gated by an RBAC
// permission key; a section with no visible items is hidden entirely.
// `label: null` = pinned standalone item(s) rendered without a header.
const NAV_GROUPS = [
  {
    label: null,
    items: [
      { path: '/', icon: LayoutDashboard, label: 'Dashboard', permission: 'dashboard:view' },
    ],
  },
  {
    label: 'Floor Ops',
    items: [
      { path: '/table-manager', icon: Grid3X3, label: 'Table Manager', permission: 'tables:view' },
      { path: '/randomizer', icon: Dices, label: 'Randomizer', permission: 'tables:view' },
      { path: '/session-corrections', icon: Clock, label: 'Play-Time Corrections', permission: 'tables:manage' },
      { path: '/waitlist', icon: Clock, label: 'Waitlist', permission: 'waitlist:view' },
      { path: '/games', icon: Gamepad2, label: 'Games Manager', permission: 'games:view' },
      { path: '/beacons', icon: Radio, label: 'Beacons', permission: 'settings:manage' },
    ],
  },
  {
    label: 'Point of Sale',
    items: [
      { path: '/pos', icon: ShoppingCart, label: 'Register', permission: 'pos:operate' },
      { path: '/pos-kitchen', icon: ChefHat, label: 'Kitchen Display', permission: 'pos:kitchen' },
      { path: '/pos-orders', icon: Receipt, label: 'Orders', permission: 'pos:operate' },
      { path: '/pos-menu', icon: UtensilsCrossed, label: 'Menu', permission: 'pos:manage' },
      { path: '/pos-reports', icon: BarChart3, label: 'Reports', permission: 'pos:manage' },
    ],
  },
  {
    label: 'Players',
    items: [
      { path: '/users', icon: User, label: 'User Manager', permission: 'users:view' },
      { path: '/restrictions', icon: Ban, label: 'Self-Exclusion', permission: 'users:edit' },
      { path: '/notifications', icon: MessageSquare, label: 'Communication Hub', permission: 'notifications:view' },
      { path: '/notification-templates', icon: MessageSquareText, label: 'Notification Messages', permission: 'notifications:send' },
      { path: '/review-hub', icon: Star, label: 'Review Hub', permission: 'review_hub:manage' },
    ],
  },
  {
    label: 'Cage',
    items: [
      { path: '/cage', icon: Wallet, label: 'Player Accounts', permission: 'cage:view' },
      { path: '/cage-balance', icon: Scale, label: 'Cage Balancing', permission: 'cbs:view' },
    ],
  },
  {
    label: 'Compliance',
    items: [
      { path: '/cage-mtl', icon: ClipboardList, label: 'Title 31 MTL', permission: 'title31:view' },
      { path: '/surveillance', icon: Video, label: 'Surveillance Log', permission: 'surveillance:view' },
    ],
  },
  {
    label: 'Loyalty & Promos',
    items: [
      { path: '/rewards', icon: Gift, label: 'Rewards Manager', permission: 'rewards:manage' },
      { path: '/redemptions', icon: Receipt, label: 'Redemption Log', permission: 'redemptions:view' },
      { path: '/achievements', icon: Trophy, label: 'Achievements', permission: 'achievements:manage' },
      { path: '/status-system', icon: Crown, label: 'Status System', permission: 'status_system:manage' },
      { path: '/leaderboards', icon: ListOrdered, label: 'Leaderboards', permission: 'settings:manage' },
      { path: '/social-rewards', icon: AtSign, label: 'Social Rewards', permission: 'rewards:manage' },
      { path: '/ads', icon: ImageIcon, label: 'Ad Manager', permission: 'ads:view' },
    ],
  },
  {
    label: 'Staff & Access',
    items: [
      { path: '/staff', icon: UserCog, label: 'Staff Manager', permission: 'staff:manage' },
      { path: '/staff-referrals', icon: UserPlus, label: 'Staff Referrals', permission: 'staff_referrals:view' },
      { path: '/roles', icon: KeyRound, label: 'Roles & Permissions', permission: 'admins:manage' },
      { path: '/admins', icon: Shield, label: 'Admin Users', permission: 'admins:view' },
    ],
  },
  {
    label: 'Insights',
    items: [
      { path: '/reports', icon: FileText, label: 'Reports', permission: 'reports:view' },
      { path: '/tournament-prepays', icon: Ticket, label: 'Tournament Pre-Reg', permission: 'tournaments:prepay:view' },
      { path: '/activity', icon: Activity, label: 'Activity Log', permission: 'activity_log:view' },
    ],
  },
  {
    label: 'System',
    items: [
      { path: '/casino-info', icon: Building, label: 'Casino Info', permission: 'casino_info:manage' },
      { path: '/settings', icon: Settings, label: 'App Settings', permission: 'settings:manage' },
      { path: '/features', icon: SlidersHorizontal, label: 'Features', permission: 'settings:manage', platformOnly: true },
      { path: '/content', icon: Type, label: 'App Copy', permission: 'settings:manage' },
      { path: '/jobs', icon: TestTube, label: 'Jobs Testing', permission: 'jobs:run' },
      { path: '/dev-tools', icon: Code, label: 'Developer Tools', permission: 'dev_tools:access' },
    ],
  },
];

const OPEN_GROUPS_KEY = 'edh_admin_nav_open_groups';

export default function Layout() {
  const { user, logout, can, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) || '{}');
    } catch {
      return {};
    }
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Does this nav path match the current route? (exact, or a sub-route)
  const pathMatches = (p) =>
    p === '/'
      ? location.pathname === '/'
      : location.pathname === p || location.pathname.startsWith(p + '/');

  // The section containing the active route — auto-expanded by default.
  const activeGroupLabel = NAV_GROUPS.find(
    (g) => g.label && g.items.some((i) => pathMatches(i.path))
  )?.label;

  // A section is open if the user toggled it; otherwise only the active one is.
  const isGroupOpen = (label) =>
    label in openGroups ? openGroups[label] : label === activeGroupLabel;

  const toggleGroup = (label) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [label]: !(label in prev ? prev[label] : label === activeGroupLabel) };
      try {
        localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const renderItem = (item) => (
    <NavLink
      key={item.path}
      to={item.path}
      end={item.path === '/'}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
          isActive
            ? 'bg-amber-500/20 text-amber-500'
            : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
        }`
      }
      onClick={() => setSidebarOpen(false)}
    >
      <item.icon size={20} />
      <span>{item.label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-gray-900 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-gray-800 transform
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 transition-transform duration-200 ease-in-out flex flex-col`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
              <span className="text-black font-bold text-sm">EDH</span>
            </div>
            <span className="text-white font-semibold">Admin Panel</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV_GROUPS.map((group, gi) => {
            const visible = group.items.filter((i) => (!i.permission || can(i.permission)) && (!i.platformOnly || isPlatformAdmin));
            if (visible.length === 0) return null;

            // Pinned standalone items (no section header).
            if (!group.label) {
              return <div key={`g${gi}`} className="space-y-1">{visible.map(renderItem)}</div>;
            }

            const open = isGroupOpen(group.label);
            return (
              <div key={group.label} className="pt-2">
                <button
                  onClick={() => toggleGroup(group.label)}
                  className="w-full flex items-center justify-between px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <span>{group.label}</span>
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
                  />
                </button>
                {open && <div className="space-y-1 mt-1">{visible.map(renderItem)}</div>}
              </div>
            );
          })}
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
              <span className="text-amber-500 font-medium">
                {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.email}</p>
              <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
          >
            <LogOut size={18} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar (mobile) */}
        <header className="lg:hidden h-16 bg-gray-800 border-b border-gray-700 flex items-center px-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-400 hover:text-white"
          >
            <Menu size={24} />
          </button>
          <span className="ml-4 text-white font-semibold">EDH Casino Admin</span>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <div className="mx-auto w-full max-w-[1400px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
